import React, { useEffect, useState } from 'react';
/**
 * `SelectInput` is a reusable dropdown component with support for:
 * - optional label (standard or floating-label layout)
 * - default "(null)" option handling via `SelectInputHelper.NullValue()`
 * - forcing a default selection when the current value is missing (`forceDefault`)
 * - defaulting a single available option (`defaultIfOneOption`)
 * - option grouping via `<optgroup>`
 * - readonly (disabled) mode and multi-select
 *
 * `SelectInputHelper` provides helper predicates for working with the
 * "(null)" sentinel value.
 */
import '../validation.scss';
import InputLabel from '../InputLabel/InputLabel';

export type SelectOption = {
  id: string;
  text: string;
  value: string | number;
  disabled?: boolean;
  group?: string;
};

export type SelectGroup = {
  id: string;
  name: string;
  disabled?: boolean;
};

export type SelectInputProps = {
  /** The name reference to the component */
  name: string;
  /** Optional explicit aria-label when there is no visible associated label */
  ariaLabel?: string;
  /** Additional styles to wrap onto the component */
  className?: string;
  /** An error message to display under the component */
  error?: string;
  /** The options that can be selected from dropdown list */
  options?: Array<SelectOption>;
  /** The groups of options if it's split into subgroups in a dropdown */
  optionGroups?: Array<SelectGroup>;
  /** The label that appears above the component */
  label?: string;
  /** The text that's displayed while waiting for an api to populate the dropdown */
  loadingText?: string;
  /** The text that's displayed by default. The standard value is 'Select...' */
  defaultText?: string;
  /** The current value of the component */
  value: string;
  /** Allows multiple options to be selected */
  multiSelect?: boolean;
  /** Decides if the floating label style is used */
  floatingLabel?: boolean;
  /** Decides if the component is editable */
  readonly?: boolean;
  /** Decides if default option is forced to the top */
  forceDefault?: boolean;
  /** If no default option is found and there is only 1 option available, then the one option will be selected, forceDefault must be true and readonly must be false */
  defaultIfOneOption?: boolean;
  /** Decides if the default option is present */
  addDefaultOption?: boolean;
  /** A function to be called on every key stroke to pass the value back up to it's controller */
  onChange: (selectedValue: string) => void;
  /** A boolean to not show loading if zero records found */
  allowZeroOptions?: boolean;
};

/** A dropdown list that allows the selection of single/multiple values. Also allows for groups within 1 select input to organise large sets of data */
const SelectInput = ({
  name,
  ariaLabel,
  className,
  error,
  options,
  optionGroups,
  label,
  loadingText = 'Loading...',
  defaultText = 'Select...',
  value,
  multiSelect,
  floatingLabel,
  readonly,
  forceDefault,
  defaultIfOneOption,
  addDefaultOption,
  onChange,
  allowZeroOptions
}: SelectInputProps): JSX.Element => {
  const [optionsLength, setOptionsLength] = useState(0);

  useEffect(() => {
    if (options !== undefined && options.length > 0) {
      setOptionsLength(options.length);
      setToDefaultWhenValueMissing(options);
    }
  }, []);

  //Options have most likely changed (only based on length though, not the best check)
  useEffect(() => {
    if (options === undefined) return;

    const optionsChanged = options.length !== optionsLength;
    setOptionsLength(options.length);
    if (optionsChanged) {
      setToDefaultWhenValueMissing(options);
    }
  }, [options]);

  const onChangeEvent = (event: React.FormEvent<HTMLSelectElement>) => {
    const selectedvalue = event.currentTarget.value;
    onChange(selectedvalue);
  };

  const setToDefaultWhenValueMissing = (valueOptions: Array<SelectOption>) => {
    if (forceDefault) {
      if (!readonly) {
        const defaultIndex = valueOptions.findIndex((o) => o.value == value);

        if (defaultIndex === -1) {
          const nullValue = SelectInputHelper.NullValue();

          const noDefaultValueOptions = valueOptions.filter(
            (o) => o.value !== nullValue
          );

          if (defaultIfOneOption && noDefaultValueOptions.length === 1) {
            onChange(noDefaultValueOptions[0].value.toString());
          } else {
            onChange(nullValue);
          }
        }
      }
    }
  };

  const getOptgroupTags = (groups: Array<SelectGroup>) => {
    if (options === undefined || options.length <= 0) {
      console.error(
        'Attempted to render select options with groups but no options were provided'
      );
      return [];
    }

    const optgroups = groups.map((group) => {
      const optionsByGroup = options?.filter((v) => {
        return v.group === group.id;
      });
      const optionElements = getOptionTags(optionsByGroup);

      let optgroupTag = <></>;
      if (optionElements.length > 0)
        optgroupTag = (
          <optgroup disabled={group.disabled} key={group.id} label={group.name}>
            {optionElements}
          </optgroup>
        );

      return optgroupTag;
    });

    return optgroups;
  };

  const getOptionTags = (
    selectOptions: Array<SelectOption>
  ): Array<JSX.Element> => {
    const optionValues: Array<JSX.Element> = [];
    if (addDefaultOption) {
      optionValues.push(
        <option data-testid={-1} key={-1} value={SelectInputHelper.NullValue()}>
          {defaultText}
        </option>
      );
    }
    const mappedOptions = selectOptions.map((option) => {
      return (
        <option
          data-testid={option.id}
          disabled={option.disabled}
          key={option.id}
          value={option.value}>
          {option.text}
        </option>
      );
    });

    const finalOptions = [...optionValues, ...mappedOptions];

    return finalOptions;
  };

  const getOptionValues = (): Array<JSX.Element> => {
    let selectOpts: Array<JSX.Element>;
    if (optionGroups && optionGroups.length > 0)
      selectOpts = getOptgroupTags(optionGroups);
    else if (options && (options.length > 0 || allowZeroOptions)) {
      selectOpts = getOptionTags(options);
    } else {
      selectOpts = [
        <option key={1} value={SelectInputHelper.NullValue()}>
          {loadingText}
        </option>
      ];
    }

    return selectOpts;
  };

  let styleWrapper = 'form-group';
  styleWrapper += error ? ' has-error' : '';

  let selectInnerStyle = 'form-select form-control';
  if (floatingLabel && label) {
    selectInnerStyle += error ? ' is-invalid' : '';
  }

  const selectOptions = getOptionValues();

  const classes = `field ${className}`;

  const createLabel = (): JSX.Element => {
    if (label !== undefined) {
      return <InputLabel labelText={label} name={name} />;
    }
    return <></>;
  };

  const createRenderPanel = () => {
    return (
      <select
        id={name}
        aria-label={ariaLabel}
        key={value}
        value={value}
        className={selectInnerStyle}
        multiple={multiSelect}
        name={name}
        onChange={onChangeEvent}
        role='list'
        disabled={readonly}>
        {selectOptions}
      </select>
    );
  };

  const labelControl = createLabel();
  const selectControl = createRenderPanel();

  const createFinalControl = () => {
    if (floatingLabel && label) {
      let wrapper = 'form-floating field';
      let errorColourStyle = '';

      if (error) {
        wrapper += ' has-error ';
        errorColourStyle = 'floating-label-validation-color';
      }

      return (
        <div>
          <div className={wrapper}>
            {selectControl}
            <label htmlFor={name}>{label}</label>
          </div>
          <div className={errorColourStyle}>{error}</div>
        </div>
      );
    } else {
      return (
        <div className={styleWrapper}>
          {labelControl}
          <div className={classes}>{selectControl}</div>
          <div aria-label='error-section' className='input'>
            {error}
          </div>
        </div>
      );
    }
  };

  const finalControl = createFinalControl();

  return <>{finalControl}</>;
};

export default SelectInput;

export class SelectInputHelper {
  static NullValue() {
    return '(null)';
  }

  static isNullValue(value: string | null) {
    const isNullValue =
      value === SelectInputHelper.NullValue() || value === null;
    return isNullValue;
  }

  static isEmptyValue(value: string) {
    const isEmptyValue = value === '';
    return isEmptyValue;
  }

  static isValidValue(value: string | null) {
    const isValid = !SelectInputHelper.isNullValue(value);
    return isValid;
  }
}
